import React, { useState, useRef, useEffect } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import DoubleArrowRightIcon from './DoubleArrowRightIcon.svg?react'
import DoubleArrowRightSuccessIcon from './DoubleArrowRightSuccessIcon.svg?react'
import { useTranslate } from '@repo/unicity'

interface SlideToPayProps {
    onSlideComplete?: () => void
    disabled?: boolean
    text?: string
    reset?: boolean // Add reset prop to allow external reset
}

const SlideToPay: React.FC<SlideToPayProps> = ({ onSlideComplete, disabled = false, text = 'slide_to_pay', reset = false }) => {
    const { translate } = useTranslate()
    const theme = useTheme()
    const [isDragging, setIsDragging] = useState(false)
    const [slidePosition, setSlidePosition] = useState(0)
    const [isCompleted, setIsCompleted] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)
    const sliderRef = useRef<HTMLDivElement>(null)
    const startPositionRef = useRef(0)
    const isCompletedRef = useRef(false) // Use ref to track completion state immediately
    const lastContainerWidthRef = useRef(0) // Track container width for resize handling

    const SLIDER_WIDTH = 60 // Width of the slider button
    const COMPLETION_THRESHOLD = 0.95 // 85% of the way to complete

    const handleStart = (clientX: number) => {
        if (disabled || isCompletedRef.current) return
        setIsDragging(true)
        startPositionRef.current = clientX - slidePosition
    }

    const handleMove = (clientX: number) => {
        if (!isDragging || disabled || isCompletedRef.current) return

        const container = containerRef.current
        if (!container) return

        const containerWidth = container.offsetWidth
        const maxPosition = containerWidth - SLIDER_WIDTH
        const newPosition = Math.max(0, Math.min(maxPosition, clientX - startPositionRef.current))

        setSlidePosition(newPosition)

        // Check if slide is completed
        if (newPosition >= maxPosition * COMPLETION_THRESHOLD && !isCompletedRef.current) {
            isCompletedRef.current = true
            setIsCompleted(true)
            setSlidePosition(maxPosition)
            onSlideComplete?.()
        }
    }

    const handleEnd = () => {
        if (!isDragging) return
        setIsDragging(false)

        // Don't reset if already completed
        if (isCompletedRef.current) return

        const container = containerRef.current
        if (!container) return

        const containerWidth = container.offsetWidth
        const maxPosition = containerWidth - SLIDER_WIDTH

        // Only reset if not completed and position is below threshold
        if (slidePosition < maxPosition * COMPLETION_THRESHOLD) {
            setSlidePosition(0)
        }
    }

    // Mouse events
    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault()
        handleStart(e.clientX)
    }

    const handleMouseMove = (e: MouseEvent) => {
        handleMove(e.clientX)
    }

    const handleMouseUp = () => {
        handleEnd()
    }

    // Touch events
    const handleTouchStart = (e: React.TouchEvent) => {
        if (e.touches[0]) handleStart(e.touches[0].clientX)
    }

    const handleTouchMove = (e: TouchEvent) => {
        e.preventDefault()
        if (e.touches[0]) handleMove(e.touches[0].clientX)
    }

    const handleTouchEnd = () => {
        handleEnd()
    }

    // Retry errors may keep reset true across consecutive completed slides.
    useEffect(() => {
        if (reset) {
            setSlidePosition(0)
            setIsCompleted(false)
            setIsDragging(false)
            isCompletedRef.current = false // Reset the ref as well
        }
    }, [reset, isCompleted])

    // Handle resize and orientation changes
    useEffect(() => {
        const handleResize = () => {
            const container = containerRef.current
            if (!container) return

            const currentWidth = container.offsetWidth
            const lastWidth = lastContainerWidthRef.current

            // If container width changed and we have a previous width
            if (lastWidth > 0 && currentWidth !== lastWidth && slidePosition > 0) {
                // Calculate the ratio and adjust slide position proportionally
                const progressRatio = slidePosition / (lastWidth - SLIDER_WIDTH)
                const newMaxPosition = currentWidth - SLIDER_WIDTH
                const newSlidePosition = Math.min(progressRatio * newMaxPosition, newMaxPosition)

                setSlidePosition(newSlidePosition)

                // If was completed and still should be completed
                if (isCompletedRef.current) {
                    setSlidePosition(newMaxPosition)
                }
            }

            lastContainerWidthRef.current = currentWidth
        }

        // Set initial width
        if (containerRef.current) {
            lastContainerWidthRef.current = containerRef.current.offsetWidth
        }

        window.addEventListener('resize', handleResize)
        // Also listen for orientation change
        window.addEventListener('orientationchange', () => {
            setTimeout(handleResize, 100) // Small delay to ensure layout has updated
        })

        return () => {
            window.removeEventListener('resize', handleResize)
            window.removeEventListener('orientationchange', handleResize)
        }
    }, [slidePosition])

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove)
            document.addEventListener('mouseup', handleMouseUp)
            document.addEventListener('touchmove', handleTouchMove, { passive: false })
            document.addEventListener('touchend', handleTouchEnd)
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove)
            document.removeEventListener('mouseup', handleMouseUp)
            document.removeEventListener('touchmove', handleTouchMove)
            document.removeEventListener('touchend', handleTouchEnd)
        }
    }, [isDragging])

    const getContainerOffsetWidth = () => {
        if (containerRef.current) {
            return containerRef.current.offsetWidth
        }
        return 0
    }

    const progressPercentage = () => {
        return getContainerOffsetWidth() ? (slidePosition / (getContainerOffsetWidth() - SLIDER_WIDTH)) * 100 : 0
    }

    return (
        <Box
            ref={containerRef}
            sx={{
                position: 'relative',
                width: {
                    xs: '203px',
                    sm: '244px'
                },
                height: '40px',
                backgroundColor: isCompleted ? 'rgba(111, 207, 151, 1)' : '#5A8FC3',
                borderRadius: '30px',
                overflow: 'hidden',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.6 : 1,
                userSelect: 'none',
                touchAction: 'none'
            }}>
            {/* Progress background */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: '0px',
                    height: '100%',
                    width: `${Math.max(progressPercentage(), 0)}%`,
                    backgroundColor: isCompleted ? 'rgba(111, 207, 151, 1)' : 'transparent',
                    borderRadius: '30px',
                    transition: isDragging ? 'none' : 'width 0.3s ease-out'
                }}
            />

            {/* Text */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: '30px',
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: !isCompleted ? 'center' : 'flex-start',
                    zIndex: 1
                }}>
                <Typography
                    variant="btn1SemiBold"
                    sx={{
                        color: theme.palette.textColor.onColor,
                        fontWeight: 600,
                        fontSize: '18px',
                        textAlign: 'center',
                        visibility: isDragging && !isCompleted ? 'hidden' : 'visible'
                    }}>
                    {translate(text)}
                </Typography>
            </Box>

            {/* Slider button */}
            <Box
                ref={sliderRef}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
                sx={{
                    position: 'absolute',
                    top: '5px',
                    left: `${slidePosition + 5 + (isCompleted ? 10 : 0)}px`,
                    right: isCompleted ? '0px' : '25px',
                    width: `40px`,
                    height: `30px`,
                    backgroundColor: theme.palette.surface.default,
                    borderRadius: '88px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: disabled ? 'not-allowed' : 'grab',
                    transition: isDragging ? 'none' : 'left 0.3s ease-out',
                    boxShadow: 'none',
                    zIndex: 2,
                    '&:active': {
                        cursor: 'grabbing'
                    }
                }}>
                {/* Arrow icons */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: theme.palette.textColor.tertiary,
                        fontSize: '16px',
                        fontWeight: 'bold'
                    }}>
                    {isCompleted ? <DoubleArrowRightSuccessIcon /> : <DoubleArrowRightIcon />}
                </Box>
            </Box>
        </Box>
    )
}

export default SlideToPay
